import type { FinancialTransaction } from './types';
import { Link } from 'react-router-dom';
import { formatDateTime, formatMoney } from './format';
import FinancialMetricLabel from './FinancialMetricLabel';

const ADJUSTMENT_LABELS = new Map<string, string>([
  ['refund', 'Refund'],
  ['refund_reversal', 'Refund reversal'],
  ['fee_adjustment', 'Fee adjustment'],
  ['tax_adjustment', 'Tax adjustment'],
  ['proration_credit', 'Upgrade proration credit'],
]);

function Detail({ label, value }: { label: string; value: string }) {
  return <><dt><FinancialMetricLabel label={label} /></dt><dd>{value}</dd></>;
}

export default function FinancialTransactionDetails({ row, embedded = false }: { row: FinancialTransaction; embedded?: boolean }) {
  return <>
    {embedded ? null : <><h2>Transaction detail</h2><p className="financial-drawer-subtitle">Read-only provider references and recorded adjustments. Row provenance does not verify every monetary field or settlement.</p></>}
    <dl className="financial-detail-list">
      <Detail label="Payment time · HKT" value={`${formatDateTime(row.occurredAt || row.createdAt)}${!row.occurredAt && row.createdAt ? ' · Creation time fallback' : ''}`} />
      <Detail label="Recorded / provider updated" value={`${formatDateTime(row.createdAt)} / ${formatDateTime(row.providerUpdatedAt)}`} />
      <Detail label="Source / status" value={`${row.source} · ${row.status}`} />
      <Detail label="Store / event kind" value={`${row.store} · ${row.eventKind}`} />
      <Detail label="Product / plan / code" value={`${row.productId || 'Not recorded'} · ${row.planId || 'Not recorded'} · ${row.offerCode || 'No recorded code'}`} />
      <Detail label="Gross" value={formatMoney(row.grossAmount, row.currency)} />
      <Detail label="Cumulative refund" value={formatMoney(row.refundedAmount, row.currency)} />
      <Detail label="Tax" value={formatMoney(row.taxAmount, row.currency)} />
      <Detail label="Platform fee" value={formatMoney(row.platformFeeAmount, row.currency)} />
      <Detail label="Provider-defined proceeds" value={formatMoney(row.proceedsAmount, row.currency)} />
      <Detail label="Row provenance" value={row.amountAccuracy || 'unknown'} />
      <Detail label="Identity" value={row.identifiedPayer ? 'Currently identifiable payer' : 'Payer identity unavailable or deleted'} />
      {row.userId ? <><dt>User profile</dt><dd><Link className="financial-user-link" to={`/users/${encodeURIComponent(row.userId)}`}>{row.userUsername ? `@${row.userUsername.replace(/^@+/, '')}` : row.userDisplayName || row.userId}</Link><small className="financial-table-secondary">{row.userId}</small></dd></> : null}
      <Detail label="Provider transaction ID" value={row.transactionId || 'Not recorded'} />
      <Detail label="Original transaction ID" value={row.originalTransactionId || 'Not recorded'} />
      <Detail label="Provider event ID" value={row.providerEventId || 'Not recorded'} />
    </dl>
    <h3>Recorded adjustments</h3>
    {row.financialAdjustments.length ? <ul className="financial-adjustments">{row.financialAdjustments.map((adjustment) => <li key={adjustment.adjustmentId}><strong>{ADJUSTMENT_LABELS.get(adjustment.type) ?? adjustment.type}</strong><span>{formatMoney(adjustment.amount, adjustment.currency)} · {formatDateTime(adjustment.occurredAt)}</span></li>)}</ul> : <p className="financial-muted">No recorded adjustments returned.</p>}
  </>;
}
